import type { GeoPoint } from './geo'
/**
 * The events module: which arenas we should be visible at, who goes, and what
 * it is worth.
 *
 * ## Collection names differ from the spec, deliberately
 *
 * The spec proposes `events`, `people` and a subcollection. This app writes
 * exclusively under the `skidaCrm` prefix — that is the additive-only contract
 * with the product's own Firestore, and `events` and `people` are exactly the
 * kind of generic names a future product feature would want. So:
 * `skidaCrmEvents`, `skidaCrmEventAssignments`, `skidaCrmPeople`. The spec
 * anticipates this: "behold repoets faktiske konvensjoner der de avviker".
 *
 * Assignments are a top-level collection rather than a subcollection, because
 * the view that matters most — who is going where, and does it clash — is a
 * query across people, not down from one event.
 */

/**
 * Pipeline stage — the same shape as a lead's, so the two boards read alike.
 *
 * The import CSV used `0 New / 1 Contacted / 2 Agreed / 3 Live / 4 Done /
 * Parked`, which mixed two different things: how far along the conversation is,
 * and whether we have decided to go. These five answer one question each, and
 * `importStage` keeps the original wording so nothing is lost in the move.
 */
export type EventStage = 'unqualified' | 'qualified' | 'planned' | 'done' | 'discarded'

export const EVENT_STAGES: { key: EventStage; label: string; hint: string }[] = [
  {
    key: 'unqualified',
    label: 'Unqualified',
    hint: 'On the list, not yet assessed. Nobody has decided whether it is worth being there.',
  },
  {
    key: 'qualified',
    label: 'Qualified',
    hint: 'Worth being at. Relevance and reach are set and the organiser is worth approaching.',
  },
  {
    key: 'planned',
    label: 'Planned',
    hint: 'Someone is going. People are assigned and a tracking code exists.',
  },
  { key: 'done', label: 'Done', hint: 'It happened. Debrief and leads collected belong here.' },
  {
    key: 'discarded',
    label: 'Discarded',
    hint: 'Deliberately not pursued. Kept so it is not proposed again next season.',
  },
]

export const EVENT_STAGE_LABEL: Record<EventStage, string> = Object.fromEntries(
  EVENT_STAGES.map((s) => [s.key, s.label]),
) as Record<EventStage, string>

/**
 * Map the import CSV's stage onto the pipeline.
 *
 * `Parked` becomes `discarded` rather than a stage of its own: the CSV used it
 * for "deliberately not pursued", which is exactly what discarded means, and a
 * sixth stage that means the same thing as the fifth is how boards rot.
 */
export function stageFromImport(raw: string): EventStage {
  const s = raw.trim().toLowerCase()
  if (s.startsWith('0')) return 'unqualified'
  if (s.startsWith('1') || s.startsWith('2')) return 'qualified'
  if (s.startsWith('3')) return 'planned'
  if (s.startsWith('4')) return 'done'
  if (s.startsWith('parked')) return 'discarded'
  return 'unqualified'
}

export type EventTier = 'A' | 'B' | 'C' | 'D'

/** Collection: `skidaCrmEvents`. Doc id is the CSV's `event_id` — EV-001 etc. */
export interface CrmEvent {
  /** Where it is, and how precisely — see `GeoPoint`. Written by the geocoder. */
  geo?: GeoPoint
  id: string
  name: string
  category: string
  country: string
  region: string
  venue: string
  /** ISO date, or '' when the organiser has not published one. */
  startDate: string
  endDate: string
  /** Free text from the source — "Confirmed", "Estimated", "Research needed". */
  dateConfidence: string
  audience: string
  website: string
  /**
   * The event's own Instagram handle, without the @.
   *
   * Here because events tag us: the Australian Backcountry Festival tagged
   * @skida.app before this field existed, and the post could not be attributed
   * to anything. An event is as capable of mentioning us as an ambassador is.
   */
  instagram: string
  stage: EventStage
  /** The CSV's own wording, kept so the migration is reversible. */
  importStage: string
  /** Who inside Skida owns the approach. A person id from `skidaCrmPeople`. */
  ownerId: string
  ownerName: string
  /** The date the work has to start by. This, not the event date, drives the queue. */
  outreachBy: string
  trackingCode: string

  /**
   * ---- What it costs and who turns up ----
   *
   * The design asks for all four. They are what turn a scored event into a
   * decision about next year: a B-tier event that cost 8,000 kr and drew 300
   * people is a better bet than an A-tier one that cost 60,000 and drew 40.
   *
   * `null` rather than 0 throughout — "we have not recorded this" and "it cost
   * nothing" are different answers, and a zero budget quietly scores as free.
   */
  budgetNok: number | null
  actualSpendNok: number | null
  registered: number | null
  attendees: number | null
  /**
   * Where the facts came from. Starting with `NOT VERIFIED` keeps the row out
   * of scoring entirely and into its own list.
   */
  sourceVerified: string
  notes: string
  nextAction: string
  contactName: string
  contactEmail: string
  contactPhone: string
  costTier: string
  sponsorshipModel: string

  /** Geocoded from `venue` + `region` + `country`. Null until that has run. */
  lat: number | null
  lng: number | null

  /** ---- Derived. Written only by the scoring pass, never by a human. ---- */
  relevance: number | null
  reach: number | null
  coverage: number | null
  eventScore: number | null
  tier: EventTier | null
  costEstimateEur: number | null
  scorePerKEur: number | null
  scoredAt: string
}

/** Collection: `skidaCrmEventAssignments`. Doc id is `${eventId}__${personId}`. */
export interface EventAssignment {
  id: string
  eventId: string
  personId: string
  personName: string
  role: 'ambassador' | 'staff' | 'partner'
  status: 'proposed' | 'confirmed' | 'declined' | 'attended'
  travelFrom: string
  costEur: number | null
  tasks: string[]
  debrief: string
  leadsCollected: number | null
  /**
   * Set by the repository before scoring: does this assignment's event clash
   * with the one being scored? Kept here rather than recomputed inside the
   * scorer so the pure function needs no calendar of its own.
   */
  overlapsEvent?: boolean
}

/** Collection: `skidaCrmPeople`. Ambassadors and staff. */
export interface Person {
  /** Where it is, and how precisely — see `GeoPoint`. Written by the geocoder. */
  geo?: GeoPoint
  id: string
  name: string
  email: string
  phone: string
  homeBase: { lat: number | null; lng: number | null; label: string }
  countries: string[]
  maxTravelKm: number | null
  languages: string[]
  /** ISO date ranges the person is unavailable — `{from,to}` inclusive. */
  blackoutDates: { from: string; to: string }[]
  active: boolean
  /** Set when the person is also a company in the lead pipeline. */
  linkedLeadId: string
  /**
   * How they came to us. `staff` is a CRM user synced in so they can be
   * assigned to events; `ambassador` applied through the interest form.
   */
  role: 'ambassador' | 'pilot' | 'staff'
  /** Their own words for where they ski. Not a residence — see the importer. */
  homeArea: string

  /**
   * ---- Running the ambassador programme ----
   *
   * The design calls this "the full control record needed to actually run the
   * ambassador program (send merch, contact them, credit their real handles),
   * not just report on it."
   *
   * Several of these were already being written by the importer before the type
   * knew about them — postalAddress, tshirt, instagram, instagramFollowers.
   * Declaring them is catching the type up with the data, not adding fields.
   */
  postalAddress: string
  tshirt: string
  /** One line in their own words, from the interest form. */
  oneLiner: string
  website: string
  /** Where the record came from — an interest form, a CRM user sync, an import. */
  source: string

  /**
   * Where they are in the programme, which is not the same as `active`.
   * `active` is a switch on the record; this is the relationship.
   */
  status: 'prospect' | 'active' | 'paused' | 'ended'
  /** Free text: product, fee, affiliate, none. Not an enum — it varies per deal. */
  compensation: string
  /** Their tracking code, matching a real code in the product's PromoCodes. */
  campaignCode: string
  /** What they have actually delivered, in their own terms. */
  contentDelivered: string
  /**
   * Their folder in the shared Medialibrary drive — 29 Aug 2026.
   *
   * Bjarte chose to create the folders by hand, so this holds the link rather
   * than generating one. It is a staff field on purpose: an ambassador reads it
   * and uploads to it, but pointing their own record at a different folder is
   * not something they should be able to do, so it is absent from the field
   * list the Firestore rule lets them write.
   */
  driveFolderUrl: string

  /** Per-channel handles, stored bare — no @, no URL. */
  instagram: string
  facebook: string
  tiktok: string
  youtube: string
  linkedin: string
  /**
   * Followers on Instagram only. The other platforms need their own APIs, which
   * the handoff withholds pending a decision — a hand-typed number for each
   * would rot silently, so they are deliberately absent.
   */
  instagramFollowers: number | null
  /**
   * Read from Instagram's Business Discovery, never typed and never scraped.
   *
   * Absent for anyone on a personal account: Business Discovery answers for
   * professional accounts only, and reading a profile page any other way breaks
   * Meta's terms. A blank here means "not a professional account", not
   * "no followers".
   */
  instagramBio: string
  instagramWebsite: string
  /** Whether their bio or link points at us at all — the partnership's point. */
  instagramMentionsSkida: boolean
  instagramFetchedAt: string | null
  /**
   * Whether Instagram answers for this account at all.
   *
   * Business Discovery covers professional accounts only, so a refusal is
   * itself the answer rather than a failure. It matters because a blank
   * follower count means two different things: "nothing fetched yet" on a
   * professional account, and "nothing will ever be fetched" on a personal one.
   *
   * Written by the collector, never typed. Empty until it has run for them.
   */
  igAccountType: 'professional' | 'personal' | ''
}

/**
 * A blank ambassador, for the create path decision 71 requires.
 *
 * Every field is present and empty rather than left undefined, because the
 * modal binds inputs straight to them and React would switch each one from an
 * uncontrolled to a controlled input on the first keystroke.
 *
 * The Instagram fields are here for the same completeness but are never saved:
 * the nightly Business Discovery pass owns them, and `updatePerson` deliberately
 * refuses them from the editor.
 */
export const EMPTY_PERSON: Person = {
  id: '',
  name: '',
  email: '',
  phone: '',
  homeBase: { lat: null, lng: null, label: '' },
  countries: [],
  maxTravelKm: null,
  languages: [],
  blackoutDates: [],
  active: true,
  linkedLeadId: '',
  role: 'ambassador',
  homeArea: '',
  postalAddress: '',
  tshirt: '',
  oneLiner: '',
  website: '',
  source: '',
  status: 'active',
  compensation: '',
  campaignCode: '',
  contentDelivered: '',
  driveFolderUrl: '',
  instagram: '',
  facebook: '',
  tiktok: '',
  youtube: '',
  linkedin: '',
  instagramFollowers: null,
  instagramBio: '',
  instagramWebsite: '',
  instagramMentionsSkida: false,
  instagramFetchedAt: null,
  igAccountType: '',
}

/**
 * Where an ambassador stands in the programme.
 *
 * Ordered by programme position, not alphabetically — the status column sorts
 * by index into this array, so prospect leads: somebody being courted comes
 * before somebody signed. Same shape and the same blue-grey as a partner in
 * conversation, which is the same stage of the same kind of relationship.
 */
export const AMBASSADOR_STATUSES: {
  key: Person['status']
  label: string
  color: string
  bg: string
}[] = [
  { key: 'prospect', label: 'Prospect', color: '#4c5b82', bg: '#f1f2f3' },
  { key: 'active', label: 'Active', color: '#256820', bg: '#eaf7e7' },
  { key: 'paused', label: 'Paused', color: '#7b5200', bg: '#fff3d8' },
  { key: 'ended', label: 'Ended', color: '#6b7280', bg: '#f1f2f3' },
]

// ---------------------------------------------------------------------------
// Scoring. Pure functions, no Firestore — the spec asks for this explicitly so
// it can be tested, and it is the part most likely to be argued about.
// ---------------------------------------------------------------------------

/** Great-circle distance in kilometres. */
export function haversineKm(
  a: { lat: number; lng: number },
  b: { lat: number; lng: number },
): number {
  const R = 6371
  const toRad = (d: number) => (d * Math.PI) / 180
  const dLat = toRad(b.lat - a.lat)
  const dLng = toRad(b.lng - a.lng)
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2
  return 2 * R * Math.asin(Math.sqrt(s))
}

function overlaps(aFrom: string, aTo: string, bFrom: string, bTo: string): boolean {
  if (!aFrom || !bFrom) return false
  return aFrom <= (bTo || bFrom) && bFrom <= (aTo || aFrom)
}

export interface CoverageInput {
  event: Pick<CrmEvent, 'id' | 'startDate' | 'endDate' | 'country' | 'lat' | 'lng'>
  people: Person[]
  assignments: EventAssignment[]
}

/**
 * Coverage — can we actually be there?
 *
 * Computed, never set by hand. The bands come straight from the spec; the one
 * judgement added here is that a missing start date returns null rather than a
 * number, because the spec says unverified dates are not scored and an
 * availability check against no date would be theatre.
 */
export function coverageFor({ event, people, assignments }: CoverageInput): number | null {
  if (!event.startDate) return null

  const busy = (p: Person): boolean => {
    if (p.blackoutDates.some((b) => overlaps(event.startDate, event.endDate, b.from, b.to)))
      return true
    // Confirmed somewhere else on overlapping dates. The caller passes every
    // assignment, not just this event's, precisely so this can be seen.
    return assignments.some(
      (a) =>
        a.personId === p.id &&
        a.eventId !== event.id &&
        a.status === 'confirmed' &&
        a.overlapsEvent === true,
    )
  }

  const here = assignments.filter((a) => a.eventId === event.id)
  const signedUp = here.some((a) => a.status === 'confirmed' || a.status === 'proposed')
  const anyoneSaidYes = here.some((a) => a.status === 'confirmed')

  const available = people.filter((p) => p.active && !busy(p))
  if (available.length === 0 && !anyoneSaidYes) return 0

  const distances = available
    .filter((p) => p.homeBase.lat !== null && p.homeBase.lng !== null)
    .filter(() => event.lat !== null && event.lng !== null)
    .map((p) =>
      haversineKm(
        { lat: p.homeBase.lat as number, lng: p.homeBase.lng as number },
        { lat: event.lat as number, lng: event.lng as number },
      ),
    )
  const nearest = distances.length ? Math.min(...distances) : Infinity

  if (nearest < 100) return 5
  if (nearest < 300 || signedUp) return 4
  const sameCountry = available.some((p) =>
    p.countries.map((c) => c.toLowerCase()).includes(event.country.toLowerCase()),
  )
  if (sameCountry || nearest < 1000) return 3
  if (anyoneSaidYes) return 2
  return 1
}

export interface ScoreInput {
  relevance: number | null
  reach: number | null
  coverage: number | null
  costEstimateEur: number | null
  stage: EventStage
  sourceVerified: string
}

export interface ScoreResult {
  eventScore: number | null
  tier: EventTier | null
  scorePerKEur: number | null
  /** Why it was not scored, for the UI to show instead of a blank. */
  skipped: string
}

export function isUnverified(sourceVerified: string): boolean {
  return sourceVerified.trim().toUpperCase().startsWith('NOT VERIFIED')
}

/**
 * `eventScore = 4·relevance + 3·reach + 3·coverage`, capped at 100.
 *
 * Cost stays out of it. A dear arena can be the right one, and folding price
 * into the score would quietly make "cheap" look like "good" — so the trade-off
 * is expressed separately as score per thousand euro, and the caller chooses
 * which column to sort on.
 */
export function scoreEvent(i: ScoreInput): ScoreResult {
  const none = { eventScore: null, tier: null, scorePerKEur: null }
  if (i.stage === 'discarded') return { ...none, skipped: 'Discarded — deliberately not pursued' }
  if (isUnverified(i.sourceVerified)) return { ...none, skipped: 'Source not verified' }
  if (i.coverage === null) return { ...none, skipped: 'No confirmed date — not scored' }
  if (i.relevance === null || i.reach === null) {
    return { ...none, skipped: 'Relevance or reach not set' }
  }

  const eventScore = Math.min(100, 4 * i.relevance + 3 * i.reach + 3 * i.coverage)
  const tier: EventTier =
    eventScore >= 75 ? 'A' : eventScore >= 55 ? 'B' : eventScore >= 35 ? 'C' : 'D'
  const scorePerKEur =
    i.costEstimateEur === null
      ? null
      : Math.round((eventScore / Math.max(i.costEstimateEur / 1000, 0.5)) * 10) / 10
  return { eventScore, tier, scorePerKEur, skipped: '' }
}

export const EVENT_TIER_STYLES: Record<EventTier, { color: string; bg: string }> = {
  A: { color: '#256820', bg: '#eaf7e7' },
  B: { color: '#7b5200', bg: '#fff3d8' },
  C: { color: '#4c5b82', bg: '#eef0ff' },
  D: { color: '#6b7280', bg: '#f1f2f3' },
}

/**
 * Stage pill colours, matching the lead board's palette so the two read alike.
 *
 * Unqualified is deliberately the flattest of the five: it is the default every
 * event was reset to, so it should look like an absence of a decision rather
 * than a status anyone worked for.
 */
export const EVENT_STAGE_STYLES: Record<EventStage, { color: string; bg: string }> = {
  unqualified: { color: '#6b7280', bg: '#f1f2f3' },
  qualified: { color: '#4c5b82', bg: '#eef0ff' },
  planned: { color: '#7b5200', bg: '#fff3d8' },
  done: { color: '#256820', bg: '#eaf7e7' },
  discarded: { color: '#8a5a5a', bg: '#f8ecec' },
}
